/**
 * Event system for widget updates
 */

import type {
  ReadyEventDetail,
  SelectElement,
  UpdateEventDetail,
} from './types';

/**
 * Dispatch a custom update event for widget changes
 */
export function dispatchUpdateEvent(
  select: SelectElement,
  detail: Partial<UpdateEventDetail> = {}
): void {
  let selectedValues: string[] = [];
  
  if (select.multiple) {
    // For multi-select, selectedOptions is a HTMLCollection, convert to array
    selectedValues = Array.from(select.selectedOptions || [])
      .map((opt) => opt.value)
      .filter((v) => v !== '');
  } else {
    // For single-select, use value
    selectedValues = select.value ? [select.value] : [];
  }

  const evt = new CustomEvent<UpdateEventDetail>('countriesWidget:update', {
    bubbles: true,
    detail: {
      value: select.value || '',
      selectedValues,
      name: select.dataset.name || null,
      country: select.dataset.country || null,
      isSubdivision: select.classList.contains('subdivision-selection'),
      ...detail,
    },
  });
  
  select.dispatchEvent(evt);
}

/**
 * Dispatch a custom ready event once a select has been populated.
 */
export function dispatchReadyEvent(
  select: SelectElement,
  detail: Partial<ReadyEventDetail> = {}
): void {
  const selectedValues = select.multiple
    ? Array.from(select.selectedOptions || [])
        .map((opt) => opt.value)
        .filter((v) => v !== '')
    : select.value
      ? [select.value]
      : [];

  const evt = new CustomEvent<ReadyEventDetail>('countriesWidget:ready', {
    bubbles: true,
    detail: {
      value: select.value || '',
      selectedValues,
      name: select.dataset.name || null,
      country: select.dataset.country || null,
      isSubdivision: select.classList.contains('subdivision-selection'),
      type: select.classList.contains('subdivision-selection')
        ? 'subdivision'
        : 'country',
      phase: 'initial',
      ...detail,
    },
  });

  select.dispatchEvent(evt);
}

/**
 * Check if an event was initiated by the widget (not user)
 */
export function isWidgetInitiatedEvent(event: Event): boolean {
  return (event as any).isWidgetInitiated === true;
}

/**
 * Mark an event as widget-initiated
 */
export function markEventAsWidgetInitiated(event: Event): void {
  (event as any).isWidgetInitiated = true;
}

